import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

/**
 * Rotate overlay — full-viewport torus at rest, CSS rotates the image
 * from portrait (0°) to landscape (-90°) in a loop with fade.
 */
const RotateOverlay = () => {
  const containerRef = useRef(null);
  const lang = localStorage.getItem('voyx-lang') || 'fr';

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    // Render as a square so FOV stays at 75° even in portrait
    const size = Math.max(window.innerWidth, window.innerHeight);
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    camera.position.set(0, -3, -3);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(size, size);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x050505);
    container.appendChild(renderer.domElement);

    const segments = 42;
    const R = 0.19;
    let phaseOffset = 0;
    const dimLevel = 0.25;
    const torusGroup = new THREE.Group();
    scene.add(torusGroup);

    const buildGeometry = (phase) => {
      const positions = [];
      const colors = [];
      const indices = [];
      const stride = segments + 1;

      for (let i = 0; i <= segments; i++) {
        const phi = (i / segments) * 2 * Math.PI - phase;
        for (let j = 0; j <= segments; j++) {
          const omega = (j / segments) * 2 * Math.PI + phase;
          const x = R * (1 - Math.cos(phi)) * Math.sin(omega);
          const y = R * (1 - Math.cos(phi)) * Math.cos(omega);
          const z = R * Math.sin(phi);
          positions.push(x, y, z);
          colors.push(dimLevel, dimLevel, dimLevel);
        }
      }
      for (let j = 0; j <= segments; j++) {
        for (let i = 0; i < segments; i++) {
          indices.push(i * stride + j, (i + 1) * stride + j);
        }
      }
      for (let i = 0; i <= segments; i++) {
        for (let j = 0; j < segments; j++) {
          indices.push(i * stride + j, i * stride + j + 1);
        }
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
      geometry.setIndex(indices);
      return geometry;
    };

    const material = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.6 });
    let mesh = new THREE.LineSegments(buildGeometry(0), material);
    torusGroup.add(mesh);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      phaseOffset += 0.016 * 0.1;
      const oldGeo = mesh.geometry;
      mesh.geometry = buildGeometry(phaseOffset);
      oldGeo.dispose();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      mesh.geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="rotate-overlay">
      <div ref={containerRef} className="rotate-overlay__torus" />
      <p>{lang === 'fr' ? 'Tourne ton écran' : 'Rotate your screen'}</p>
    </div>
  );
};

export default RotateOverlay;
